import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import WalkInAlertsPill from "../../components/WalkInAlertsPill";
import {
    FaMapMarkerAlt,
    FaPhone,
    FaToggleOn,
    FaToggleOff,
    FaExternalLinkAlt,
    FaPlus,
    FaStore,
} from "react-icons/fa";

interface PickupLocation {
    locationId: string;
    storeName: string;
    address: string;
    coordinates: string;
    contactInfo: string;
    isActive: boolean;
    mapLink: string;
    createdAt: string;
    updatedAt: string;
}

export default function WalkInPickupLocations() {
    const [location, setLocation] = useState<PickupLocation | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const [formData, setFormData] = useState({
        storeName: "",
        address: "",
        coordinates: "",
        contactInfo: "",
        isActive: true,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const getToken = () => {
        const auth = sessionStorage.getItem("authState");
        const jsonData = JSON.parse(auth || "{}");
        return jsonData?.token as string | undefined;
    };

    const authHeaders = (token?: string) =>
        token ? { Authorization: `Bearer ${token}` } : undefined;

    const fetchLocation = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = getToken();
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/front-desk/admin/pickup-location`,
                { headers: authHeaders(token) }
            );
            if (res.data?.data) {
                setLocation(res.data.data);
            }
        } catch (err: any) {
            if (err.response?.status !== 404) {
                toast.error(
                    err.response?.data?.message || "Failed to fetch pickup location"
                );
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLocation();
    }, [fetchLocation]);

    const validate = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.storeName.trim())
            newErrors.storeName = "Store name is required";
        if (!formData.address.trim()) newErrors.address = "Address is required";
        if (!formData.coordinates.trim())
            newErrors.coordinates = "Coordinates are required";
        if (!formData.contactInfo.trim())
            newErrors.contactInfo = "Contact info is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/front-desk/admin/pickup-location`,
                formData,
                { headers: authHeaders(token) }
            );
            if (res.data) {
                toast.success("Pickup location saved successfully");
                setShowForm(false);
                setFormData({
                    storeName: "",
                    address: "",
                    coordinates: "",
                    contactInfo: "",
                    isActive: true,
                });
                setErrors({});
                fetchLocation();
            }
        } catch (err: any) {
            toast.error(
                err.response?.data?.message || "Failed to save pickup location"
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-NG", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <main className="w-full flex flex-col gap-6">
            <WalkInAlertsPill />

            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Pickup Location
                    </h1>
                    <p className="text-gray-500 text-sm">
                        Manage the physical pickup location for walk-in orders.
                    </p>
                </div>
                {!showForm && (
                    <button
                        onClick={() => {
                            if (location) {
                                setFormData({
                                    storeName: location.storeName,
                                    address: location.address,
                                    coordinates: location.coordinates,
                                    contactInfo: location.contactInfo,
                                    isActive: location.isActive,
                                });
                            }
                            setShowForm(true);
                        }}
                        className="px-4 py-2 bg-[#008060] text-white hover:bg-[#006b50] rounded-lg text-sm font-semibold flex items-center gap-2 transition-all shadow-sm"
                    >
                        <FaPlus className="text-xs" />
                        {location ? "Update Location" : "Add Location"}
                    </button>
                )}
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-3 border-[#008060] border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-gray-400">Loading pickup location...</p>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!isLoading && !location && !showForm && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 flex flex-col items-center gap-4 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center">
                        <FaMapMarkerAlt className="w-7 h-7 text-gray-300" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800 text-lg">
                            No Pickup Location Set
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">
                            Add a pickup location so customers know where to collect their
                            orders.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowForm(true)}
                        className="px-5 py-2.5 bg-[#008060] text-white hover:bg-[#006b50] rounded-lg text-sm font-semibold flex items-center gap-2 transition-all shadow-sm mt-2"
                    >
                        <FaPlus className="text-xs" />
                        Add Pickup Location
                    </button>
                </div>
            )}

            {/* Location Details Card */}
            {!isLoading && location && !showForm && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#008060]/10 rounded-lg flex items-center justify-center">
                                <FaStore className="w-5 h-5 text-[#008060]" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">
                                    {location.storeName}
                                </h3>
                                <p className="text-xs text-gray-400">
                                    ID: {location.locationId}
                                </p>
                            </div>
                        </div>
                        <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${location.isActive
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-red-50 text-red-700"
                                }`}
                        >
                            {location.isActive ? "Active" : "Inactive"}
                        </span>
                    </div>

                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="flex items-start gap-3">
                            <FaMapMarkerAlt className="text-gray-400 mt-0.5 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
                                    Address
                                </p>
                                <p className="text-sm text-gray-800 mt-1">
                                    {location.address}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <FaPhone className="text-gray-400 mt-0.5 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
                                    Contact Info
                                </p>
                                <p className="text-sm text-gray-800 mt-1">
                                    {location.contactInfo}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <FaMapMarkerAlt className="text-gray-400 mt-0.5 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
                                    Coordinates
                                </p>
                                <p className="text-sm text-gray-800 mt-1">
                                    {location.coordinates}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <FaExternalLinkAlt className="text-gray-400 mt-0.5 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
                                    Google Maps
                                </p>
                                <a
                                    href={location.mapLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-[#008060] hover:underline mt-1 inline-block"
                                >
                                    View on Map →
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center text-xs text-gray-400">
                        <span>Created: {formatDate(location.createdAt)}</span>
                        <span>Last Updated: {formatDate(location.updatedAt)}</span>
                    </div>
                </div>
            )}

            {/* Add/Update Form */}
            {showForm && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-gray-100 flex justify-between items-center">
                        <h3 className="font-bold text-lg text-gray-900">
                            {location ? "Update Pickup Location" : "Add Pickup Location"}
                        </h3>
                        <button
                            onClick={() => {
                                setShowForm(false);
                                setErrors({});
                            }}
                            className="text-gray-400 hover:text-gray-600 font-bold text-lg"
                        >
                            ✕
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Store Name */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold text-gray-700">
                                    Store Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="storeName"
                                    value={formData.storeName}
                                    onChange={handleChange}
                                    placeholder="e.g. FoodBank Pickup Center"
                                    className={`border p-2.5 focus:outline-[#008060] rounded-lg text-sm w-full ${errors.storeName ? "border-red-400" : ""
                                        }`}
                                />
                                {errors.storeName && (
                                    <span className="text-xs text-red-500">
                                        {errors.storeName}
                                    </span>
                                )}
                            </div>

                            {/* Contact Info */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold text-gray-700">
                                    Contact Info <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="contactInfo"
                                    value={formData.contactInfo}
                                    onChange={handleChange}
                                    placeholder="e.g. +2348012345678"
                                    className={`border p-2.5 focus:outline-[#008060] rounded-lg text-sm w-full ${errors.contactInfo ? "border-red-400" : ""
                                        }`}
                                />
                                {errors.contactInfo && (
                                    <span className="text-xs text-red-500">
                                        {errors.contactInfo}
                                    </span>
                                )}
                            </div>

                            {/* Address */}
                            <div className="flex flex-col gap-1.5 md:col-span-2">
                                <label className="text-xs font-semibold text-gray-700">
                                    Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="e.g. 1, Alaafin Avenue, Oluyole Estate, Ibadan"
                                    className={`border p-2.5 focus:outline-[#008060] rounded-lg text-sm w-full ${errors.address ? "border-red-400" : ""
                                        }`}
                                />
                                {errors.address && (
                                    <span className="text-xs text-red-500">
                                        {errors.address}
                                    </span>
                                )}
                            </div>

                            {/* Coordinates */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold text-gray-700">
                                    Coordinates <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="coordinates"
                                    value={formData.coordinates}
                                    onChange={handleChange}
                                    placeholder="e.g. 7.365050,3.858738"
                                    className={`border p-2.5 focus:outline-[#008060] rounded-lg text-sm w-full ${errors.coordinates ? "border-red-400" : ""
                                        }`}
                                />
                                {errors.coordinates && (
                                    <span className="text-xs text-red-500">
                                        {errors.coordinates}
                                    </span>
                                )}
                            </div>

                            {/* isActive Toggle */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold text-gray-700">
                                    Status
                                </label>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            isActive: !prev.isActive,
                                        }))
                                    }
                                    className="flex items-center gap-3 p-2.5 border rounded-lg hover:bg-gray-50 transition-colors w-fit"
                                >
                                    {formData.isActive ? (
                                        <FaToggleOn className="w-7 h-7 text-[#008060]" />
                                    ) : (
                                        <FaToggleOff className="w-7 h-7 text-gray-300" />
                                    )}
                                    <span className="text-sm font-medium text-gray-700">
                                        {formData.isActive ? "Active" : "Inactive"}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 mt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowForm(false);
                                    setErrors({});
                                }}
                                disabled={isSubmitting}
                                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 py-2.5 bg-[#008060] text-white hover:bg-[#006b50] rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isSubmitting ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : location ? (
                                    "Update Location"
                                ) : (
                                    "Add Location"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </main>
    );
}
